import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Cake, Save, Send, Users, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * BirthdayPromoCard — one active birthday-promo template per business.
 *
 * A daily background worker on the server sends this promo to every opted-in
 * customer whose birth_month_day == today (idempotent per year). The business
 * only needs to keep the template up to date — no per-customer scheduling.
 */
export default function BirthdayPromoCard({ marketingActive, subscriptionActive }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [form, setForm] = useState({
    active: false,
    title: "Happy Birthday from us!",
    description:
      "Hey {first_name}, wishing you an amazing birthday. Enjoy a small treat on us — flash this code in store or use it online.",
    discount_code: "BDAY15",
    days_valid: 30,
  });
  const [audience, setAudience] = useState({ total_with_birthday: 0, celebrating_today: 0, today: "" });

  const canConfigure = subscriptionActive && marketingActive;

  const load = async () => {
    setLoading(true);
    try {
      const [tpl, aud] = await Promise.all([
        api.get("/me/business/birthday-promo"),
        api.get("/me/business/birthday-promo/audience").catch(() => ({ data: null })),
      ]);
      if (tpl.data) {
        setForm((prev) => ({
          ...prev,
          active: !!tpl.data.active,
          title: tpl.data.title || prev.title,
          description: tpl.data.description || prev.description,
          discount_code: tpl.data.discount_code || "",
          days_valid: tpl.data.days_valid || 30,
        }));
      }
      if (aud.data) setAudience(aud.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canConfigure) load();
    else setLoading(false);
  }, [canConfigure]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/me/business/birthday-promo", {
        active: form.active,
        title: form.title.trim(),
        description: form.description.trim(),
        discount_code: form.discount_code.trim().toUpperCase(),
        days_valid: Math.max(1, Math.min(365, Number(form.days_valid) || 30)),
      });
      toast.success(form.active ? "Birthday promo saved & active" : "Birthday promo saved (currently paused)");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const sendNow = async () => {
    if (!window.confirm(
      `Send the birthday promo NOW to every opted-in customer with a birthday today (${audience.celebrating_today})? This is idempotent — repeat sends this year are skipped.`
    )) return;
    setTriggering(true);
    try {
      const { data } = await api.post("/me/business/birthday-promo/send-now");
      toast.success(`Dispatched ${data.sent} birthday message${data.sent === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setTriggering(false);
    }
  };

  if (!canConfigure) {
    return (
      <Card className="rounded-md border border-border p-6" data-testid="birthday-promo-card-locked">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
            <Cake size={18} />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-display text-xl">Birthday promotions</h3>
            <p className="text-sm text-gray-500 mt-0.5 max-w-2xl">
              Delight customers with an automatic discount on their birthday. Requires
              an active servrevu Pro subscription and the Marketing add-on ($35/mo) so
              we can send on your behalf.
            </p>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="rounded-md border border-border p-6" data-testid="birthday-promo-card">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="h-10 w-10 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
          <Cake size={18} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display text-xl">Birthday promotions</h3>
            <Badge
              variant="outline"
              className={`rounded-sm text-[10px] font-mono ${
                form.active ? "border-emerald-400 text-emerald-700" : "border-gray-300 text-gray-500"
              }`}
              data-testid="birthday-promo-status-badge"
            >
              {form.active ? "ACTIVE" : "PAUSED"}
            </Badge>
          </div>
          <p className="text-sm text-gray-500 mt-0.5 max-w-2xl">
            One template. We send it to every opted-in customer on their birthday and
            include a discount valid for the number of days you set below.
            Use <span className="font-mono">{"{first_name}"}</span> in the title or body to personalise.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="mt-6 flex items-center gap-2 text-sm text-gray-500">
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      ) : (
        <>
          <div className="mt-5 flex items-center gap-3 flex-wrap">
            <div className="inline-flex items-center gap-2">
              <Switch
                checked={form.active}
                onCheckedChange={(v) => setForm((f) => ({ ...f, active: !!v }))}
                data-testid="birthday-promo-active-toggle"
              />
              <span className="text-sm">Send automatically each day</span>
            </div>
            <div className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs font-mono text-gray-600">
              <Users size={12} />
              {audience.total_with_birthday} customer{audience.total_with_birthday === 1 ? "" : "s"} opted-in with birthday on file
            </div>
            <div
              className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-mono text-amber-700"
              data-testid="birthday-promo-today-count"
            >
              <Cake size={12} />
              {audience.celebrating_today} celebrating today ({audience.today})
            </div>
          </div>

          <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="bday-title" className="text-xs font-mono uppercase tracking-wider text-gray-600">
                Subject / headline
              </Label>
              <Input
                id="bday-title"
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                maxLength={120}
                className="rounded-sm mt-1"
                data-testid="birthday-promo-title-input"
              />
            </div>
            <div>
              <Label htmlFor="bday-code" className="text-xs font-mono uppercase tracking-wider text-gray-600">
                Discount code
              </Label>
              <Input
                id="bday-code"
                value={form.discount_code}
                onChange={(e) =>
                  setForm((f) => ({ ...f, discount_code: e.target.value.toUpperCase().slice(0, 40) }))
                }
                maxLength={40}
                placeholder="BDAY15"
                className="rounded-sm mt-1 font-mono"
                data-testid="birthday-promo-code-input"
              />
            </div>
          </div>

          <div className="mt-4">
            <Label htmlFor="bday-desc" className="text-xs font-mono uppercase tracking-wider text-gray-600">
              Message body
            </Label>
            <Textarea
              id="bday-desc"
              rows={5}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              maxLength={1500}
              className="rounded-sm mt-1"
              data-testid="birthday-promo-desc-input"
            />
          </div>

          <div className="mt-4">
            <Label htmlFor="bday-days" className="text-xs font-mono uppercase tracking-wider text-gray-600">
              Days the discount stays valid after the birthday
            </Label>
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              <Input
                id="bday-days"
                type="number"
                min={1}
                max={365}
                value={form.days_valid}
                onChange={(e) => setForm((f) => ({ ...f, days_valid: e.target.value }))}
                className="rounded-sm w-28 font-mono"
                data-testid="birthday-promo-days-input"
              />
              {[7, 14, 30, 60].map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, days_valid: d }))}
                  className={`px-3 py-1.5 rounded-[35px] text-xs font-mono border transition-colors ${
                    Number(form.days_valid) === d
                      ? "bg-[#B31E1E] text-white border-[#B31E1E]"
                      : "bg-white text-gray-700 border-border hover:bg-gray-50"
                  }`}
                  data-testid={`birthday-promo-days-preset-${d}`}
                >
                  {d} days
                </button>
              ))}
            </div>
            <p className="text-[11px] text-gray-500 mt-1.5">
              The message will show today + {form.days_valid || 30} days as the expiration date.
            </p>
          </div>

          <div className="mt-6 flex items-center gap-2 flex-wrap">
            <Button
              onClick={save}
              disabled={saving}
              className="rounded-[35px] shadow-sm hover:shadow-md bg-[#B31E1E] hover:bg-[#8F1717]"
              data-testid="birthday-promo-save-button"
            >
              {saving ? (
                <><Loader2 size={14} className="mr-1.5 animate-spin" /> Saving…</>
              ) : (
                <><Save size={14} className="mr-1.5" /> Save template</>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={sendNow}
              disabled={triggering || audience.celebrating_today === 0}
              className="rounded-[35px]"
              data-testid="birthday-promo-send-now-button"
              title={audience.celebrating_today === 0 ? "No customers celebrating today" : "Trigger now (idempotent)"}
            >
              {triggering ? (
                <><Loader2 size={14} className="mr-1.5 animate-spin" /> Sending…</>
              ) : (
                <><Send size={14} className="mr-1.5" /> Send today&apos;s batch now</>
              )}
            </Button>
          </div>
        </>
      )}
    </Card>
  );
}
